import { createSlice } from '@reduxjs/toolkit';
import qs from 'qs';
import { format, parseISO } from 'date-fns';
import * as R from 'ramda';

// utils
import http from 'utils/http';
import { setErrorWithoutSnack } from 'utils/helpers/fetch';
import { createSnack } from 'snackbar/snackbarSlice';

import { fetchOriginatorLocationProducts } from './originatorLocationProducts';
import { fetchOriginatorLocationProductBalances } from './originatorLocationProductBalances';
import { fetchOriginatorLocationProductOrigins } from './originatorLocationProductOrigins';

const initialState = {
  // origins page
  anyOrigin: true,
  // reconsignmentControl used here too
  // customer chain page
  chainContactIds: [],
  // order details page
  chained: false,
  // inventory options page
  convertBarrels: undefined,
  dataCompanyId: null,
  error: '',
  // form errors
  errorFields: {
    chainContactIds: false,
    chained: false,
    convertBarrels: false,
    dataCompanyId: false,
    inventorySelection: false,
    locationId: false,
    originIds: false,
    originatorIdName: false,
    productId: false,
    recipientIdName: false,
    reconsignmentControl: false,
    transferDate: false,
    volume: false,
  },
  inventorySelection: 'EITHER',
  locationId: null,
  originIds: [],
  originatorIdName: null,
  productId: null,
  recipientIdName: '',
  reconsignmentControl: 'EITHER',
  // optional
  referenceId: '',
  response: '',
  restrictedLocations: [],
  restrictedLocationsPending: false,
  rvp: null,
  sendError: '',
  sendPending: false,
  setReconsignmentControls: false,
  // optional
  transferDate: new Date().toJSON(),
  volume: '',
};

const reducers = {
  resetCreatePto() {
    return initialState;
  },
  sendCreatePtoDetailsError(state, { payload }) {
    return R.assoc('sendError', payload, state);
  },
  sendCreatePtoDetailsPending(state, { payload }) {
    return R.assoc('sendPending', payload, state);
  },
  sendCreatePtoDetailsResetError(state) {
    return R.assoc('sendError', '', state);
  },
  sendCreatePtoDetailsSuccess(state, { payload }) {
    return R.assoc('response', payload, state);
  },
  setRestrictedLocations(state, { payload }) {
    return R.assoc('restrictedLocations', payload, state);
  },
  setRestrictedLocationsPending(state, { payload }) {
    return R.assoc('restrictedLocationsPending', payload, state);
  },
  updateErrors(state, { payload }) {
    Object.keys(payload).forEach(key => {
      // eslint-disable-next-line no-param-reassign
      state.errorFields[key] = payload[key];
    });
  },
  updateField: {
    prepare: (key, value) => ({
      payload: { key, value },
    }),
    reducer(state, { payload }) {
      const { key, value } = payload;
      return R.assoc(`${key}`, value, state);
    },
  },
};

const { actions, reducer } = createSlice({
  initialState,
  name: 'createPto',
  reducers,
});

export const {
  setRestrictedLocations,
  setRestrictedLocationsPending,
  sendCreatePtoDetailsPending,
  sendCreatePtoDetailsSuccess,
  sendCreatePtoDetailsError,
  updateField,
  updateErrors,
  sendCreatePtoDetailsResetError,
  resetCreatePto,
} = actions;

// Actions
function validateFields(fields) {
  // check for required fields
  const errorFields = {
    chainContactIds: fields.chained && fields.chainContactIds.length === 0,
    chained: fields.chained === null,
    convertBarrels: fields.convertBarrels === null,
    dataCompanyId: !fields.dataCompanyId,
    inventorySelection: !fields.inventorySelection,
    locationId: !fields.locationId,
    originIds: !fields.originIds,
    originatorIdName: !fields.originatorIdName,
    productId: !fields.productId,
    recipientIdName: !fields.recipientIdName,
    reconsignmentControl: !fields.reconsignmentControl,
    transferDate: !fields.transferDate,
    volume: !fields.volume,
  };
  return errorFields;
}

function postBody(params) {
  const {
    dataCompanyId,
    transferDate,
    originatorIdName,
    recipientIdName,
    locationId,
    productId,
    rvp,
    volume,
    referenceId,
    inventorySelection,
    reconsignmentControl,
    convertBarrels,
    anyOrigin,
    originIds,
    chained,
    chainContactIds,
  } = params;

  return {
    AnyOrigin: anyOrigin,
    ChainContactIds: chained ? chainContactIds.map(id => id.toString()) : undefined,
    Chained: chained,
    ConvertBarrels: convertBarrels,
    DataCompanyId: dataCompanyId,
    InventorySelection: inventorySelection,
    LocationId: locationId,
    OriginIds: anyOrigin ? undefined : originIds,
    OriginatorIdName: originatorIdName,
    ProductId: productId,
    RecipientIdName: recipientIdName,
    ReconsignmentControl: reconsignmentControl,
    ReferenceId: referenceId || undefined,
    Rvp: rvp || undefined,
    TransferDate: format(parseISO(transferDate), 'yyyy-MM-dd'),
    Volume: parseFloat(volume),
  };
}

const PTO_URL = '/product-transfer-orders/';
// Actions
let fetchController;
export function fetchCopyPto(queryParams = {}) {
  return dispatch => {
    const paramString = qs.stringify(queryParams, {
      allowDots: true,
      encode: false,
    });
    dispatch(sendCreatePtoDetailsPending(true));
    if (fetchController) fetchController.abort();
    fetchController = new AbortController();
    const { signal } = fetchController;
    return http
      .get(`${PTO_URL}create?${paramString}`, { signal })
      .then(res => {
        const formattedResponse = { ...res.data };
        // account for date string, numbers, and null values
        formattedResponse.transferDate = new Date().toJSON(); // default copy to today
        formattedResponse.volume = formattedResponse.volume.toString();
        formattedResponse.chainContactIds = formattedResponse.chainContactIds
          ? formattedResponse.chainContactIds.map(c => parseInt(c, 10))
          : [];
        formattedResponse.originIds = formattedResponse.originIds || [];
        formattedResponse.referenceId = ''; // clear out reference ID
        Object.keys(formattedResponse).forEach(key => {
          dispatch(updateField(key, formattedResponse[key]));
        });
        dispatch(sendCreatePtoDetailsPending(false));
        dispatch(sendCreatePtoDetailsError(''));
        // refresh product list
        dispatch(
          fetchOriginatorLocationProducts({
            locationId: res.data.locationId,
            originator: {
              companyCustomerIdName: res.data.originatorIdName,
              dataCompanyId: res.data.dataCompanyId,
            },
          })
        );
        // refresh balances list
        dispatch(
          fetchOriginatorLocationProductBalances({
            locationId: res.data.locationId,
            originIds: res.data.anyOrigin ? null : res.data.originIds,
            originator: {
              companyCustomerIdName: res.data.originatorIdName,
              dataCompanyId: res.data.dataCompanyId,
            },
            productId: res.data.productId,
            rvp: res.data.rvp,
          })
        );
        // refresh origins list
        dispatch(
          fetchOriginatorLocationProductOrigins({
            locationId: res.data.locationId,
            originator: {
              companyCustomerIdName: res.data.originatorIdName,
              dataCompanyId: res.data.dataCompanyId,
            },
            productId: res.data.productId,
            rvp: res.data.rvp,
          })
        );
      })
      .catch(error => {
        dispatch(
          createSnack({
            message: error.response.data.title,
            options: { variant: 'error' },
          })
        );
        dispatch(sendCreatePtoDetailsPending(false));
      });
  };
}

let fetchRestrictedController;
export function fetchRestrictedLocations(params = {}) {
  const paramString = qs.stringify(params, {
    allowDots: true,
    encode: false,
  });

  if (fetchRestrictedController) fetchRestrictedController.abort();
  fetchRestrictedController = new AbortController();
  const { signal } = fetchRestrictedController;

  return async dispatch => {
    dispatch(setRestrictedLocationsPending(true));
    return http
      .get(`${PTO_URL}get-reconsignment-restricted-locations-list?${paramString}`, {
        signal,
      })
      .then(({ data }) => {
        dispatch(setRestrictedLocations(data));
        dispatch(setRestrictedLocationsPending(false));
        return data;
      })
      .catch(error => {
        if (error?.response?.data?.title) {
          dispatch(
            createSnack({
              message: error.response.data.title,
              options: { variant: 'error' },
            })
          );
          dispatch(setRestrictedLocationsPending(false));
        }
        return error;
      });
  };
}

let createController;
export function sendCreatePto(params = {}) {
  return dispatch => {
    if (Object.values(validateFields(params)).some(val => val)) {
      updateErrors(validateFields(params));
      return Promise.reject();
    }
    if (createController) createController.abort();
    createController = new AbortController();
    const { signal } = createController;
    dispatch(sendCreatePtoDetailsPending(true));
    return http
      .post(`${PTO_URL}create`, postBody(params), { signal })
      .then(res => {
        dispatch(sendCreatePtoDetailsSuccess(res.data));
        dispatch(sendCreatePtoDetailsPending(false));
        dispatch(sendCreatePtoDetailsResetError());
      })
      .catch(error => {
        dispatch(
          createSnack({
            message: error.response.data.title,
            options: { variant: 'error' },
          })
        );
        setErrorWithoutSnack(error, dispatch, sendCreatePtoDetailsError);
        dispatch(sendCreatePtoDetailsPending(false));
      });
  };
}

export function updateCreatePtoField(key, value, integerOnly = false) {
  return dispatch => {
    dispatch(updateField(key, integerOnly ? value.replace(/[^0-9]/g, '') : value));
  };
}

export function resetSendCreatePtoError() {
  return dispatch => {
    dispatch(sendCreatePtoDetailsResetError());
  };
}

export default reducer;
